import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, Search, ArrowLeft, X } from "lucide-react";
import sections from "./data/index";

function AcadenceLogo() {
  return (
    <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6">
      <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66" />
      <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5" />
      <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5" />
    </svg>
  );
}

function SidebarContent({ onSearchClick, onMobileClose }) {
  const { section: activeSection, slug: activeSlug } = useParams();

  // Determine which section is initially open (the one containing the active page)
  const initialOpen = activeSection || Object.keys(sections)[0];
  const [openSections, setOpenSections] = useState({ [initialOpen]: true });

  function toggleSection(key) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ background: "#0A1628" }}
    >
      {/* Logo area */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
        <Link to="/docs" className="flex items-center gap-2 group">
          <AcadenceLogo />
          <span
            className="font-['Syne'] font-bold text-white"
            style={{ fontSize: 15 }}
          >
            Acadence{" "}
            <span
              className="font-['DM_Sans'] font-normal rounded px-1"
              style={{ fontSize: 11, background: "rgba(75,123,245,0.2)", color: "#4B7BF5" }}
            >
              docs
            </span>
          </span>
        </Link>
        {/* Close button — mobile only */}
        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="text-white/50 hover:text-white transition-colors md:hidden"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Back to Acadence */}
      <div className="px-4 py-2 border-b border-white/10">
        <Link
          to="/"
          className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors"
          style={{ fontSize: 12 }}
        >
          <ArrowLeft size={12} />
          <span className="font-['DM_Sans']">Back to Acadence</span>
        </Link>
      </div>

      {/* Search trigger */}
      <div className="px-4 py-3 border-b border-white/10">
        <button
          onClick={onSearchClick}
          className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-white/40 hover:text-white/70 hover:bg-white/5 transition-all"
          style={{ border: "1px solid rgba(255,255,255,0.1)", fontSize: 13 }}
        >
          <Search size={14} />
          <span className="font-['DM_Sans'] flex-1 text-left">Search docs…</span>
          <span
            className="font-['DM_Sans'] rounded px-1.5 py-0.5"
            style={{ fontSize: 11, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.3)" }}
          >
            ⌘K
          </span>
        </button>
      </div>

      {/* Nav sections */}
      <div className="flex-1 overflow-y-auto py-3 space-y-1">
        {Object.entries(sections).map(([key, section]) => {
          const isOpen = !!openSections[key];
          const Icon = section.icon;
          return (
            <div key={key}>
              {/* Section header */}
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between px-4 py-1.5 group"
              >
                <span
                  className="font-['DM_Sans'] font-semibold uppercase tracking-widest text-blue-400/70"
                  style={{ fontSize: 9 }}
                >
                  {section.label}
                </span>
                <ChevronDown
                  size={12}
                  className="text-white/30 transition-transform"
                  style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}
                />
              </button>

              {/* Pages */}
              {isOpen && (
                <div className="mt-0.5 mb-2">
                  {section.pages.map((page) => {
                    const isActive =
                      activeSection === key && activeSlug === page.slug;
                    const PageIcon = page.icon;
                    return (
                      <Link
                        key={page.slug}
                        to={`/docs/${key}/${page.slug}`}
                        onClick={onMobileClose}
                        className="flex items-center gap-2.5 mx-2 px-3 py-1.5 rounded-md transition-all"
                        style={{
                          background: isActive
                            ? "rgba(75,123,245,0.15)"
                            : "transparent",
                          borderLeft: isActive
                            ? "2px solid #4B7BF5"
                            : "2px solid transparent",
                          color: isActive ? "#ffffff" : "rgba(255,255,255,0.55)",
                          fontSize: 13,
                        }}
                      >
                        {PageIcon && (
                          <PageIcon
                            size={14}
                            className="shrink-0"
                            style={{ opacity: isActive ? 1 : 0.6 }}
                          />
                        )}
                        <span className="font-['DM_Sans'] truncate">{page.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DocsSidebar({ onSearchClick, mobileOpen, onMobileClose }) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 z-30"
        style={{ width: 250 }}
      >
        <SidebarContent onSearchClick={onSearchClick} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <aside
            className="fixed left-0 top-0 bottom-0 z-50 flex flex-col md:hidden"
            style={{ width: 280 }}
          >
            <SidebarContent
              onSearchClick={() => {
                onMobileClose();
                onSearchClick();
              }}
              onMobileClose={onMobileClose}
            />
          </aside>
        </>
      )}
    </>
  );
}
